import { useState } from "react";
import {
  LoaderFunction,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { get, post } from "api";
import { Button } from "atoms/button/Button";
import ArrowLeft from "atoms/icon/icons/arrow_left.svg";
import { Text } from "atoms/Text/Text";
import {
  ReturnReasonsAndMedia,
  ReturnReasonsFormData,
} from "modules/retailProducts/ReturnReasonsAndMedia/ReturnReasonsAndMedia";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";

const load = async (orderId: string, itemId: string) => {
  const platform = await queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocation = await queryClient.fetchQuery(
    ["/platform-location"],
    () => get("/platform-location")
  );

  let retailProductOrder = null;
  if (orderId) {
    try {
      retailProductOrder = await queryClient.fetchQuery(
        ["/retailproducts/orders/{orderId}", { orderId }],
        () => get("/retailproducts/orders/{orderId}", { orderId })
      );
    } catch (error) {
      console.error("Failed to fetch retail product order:", error);
      retailProductOrder = null;
    }
  }

  return {
    platform,
    platformLocation,
    retailProductOrder,
    itemId,
  };
};

export type SelectedItemReturnLoaderData = Awaited<ReturnType<typeof load>>;
export const SelectedItemReturnLoader: LoaderFunction = async ({ params }) => {
  const orderId = params.id;
  const itemId = params.itemId;

  if (!orderId || !itemId) {
    throw new Error("Order ID and Item ID are required");
  }

  return await load(orderId, itemId);
};

export default function SelectedItemReturnFlow() {
  const location = useLocation();
  const instanceId = location.state?.instanceId;
  const { retailProductOrder, itemId, platform } =
    useLoaderData() as SelectedItemReturnLoaderData;
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ReturnReasonsFormData | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  if (!retailProductOrder) {
    return (
      <div className="flex min-h-screen flex-col justify-between bg-gradient-to-bl from-brand-color/40 via-brand-tint to-white">
        <TopNav
          startIconSVG={ArrowLeft}
          onStartIconClick={() => {
            navigate(-1);
          }}
        >
          Return Request
        </TopNav>
        <div className="flex flex-grow flex-col items-center justify-center px-6 pt-10">
          <div className="mt-6 text-center font-serif text-2xl text-primary">
            <h3>Order not found</h3>
          </div>
        </div>
      </div>
    );
  }

  const selectedItem = retailProductOrder.lineItemsForShipping.find(
    (item) => item.packageId === itemId
  );

  if (!selectedItem) {
    return (
      <div className="flex min-h-screen flex-col justify-between bg-gradient-to-bl from-brand-color/40 via-brand-tint to-white">
        <TopNav
          startIconSVG={ArrowLeft}
          onStartIconClick={() => {
            navigate(-1);
          }}
        >
          Return Request
        </TopNav>
        <div className="flex flex-grow flex-col items-center justify-center px-6 pt-10">
          <div className="mt-6 text-center font-serif text-2xl text-primary">
            <h3>Item not found</h3>
          </div>
        </div>
      </div>
    );
  }

  const handleNext = () => {
    if (currentStep === 0) {
      if (formData?.hasBeenOpenedOrUsed !== undefined && !formData.imageUrls) {
        navigate(`/wallet/products`);
      } else if (
        formData?.hasBeenOpenedOrUsed !== undefined &&
        formData?.imageUrls
      ) {
        const form = document.querySelector("form");
        if (form) {
          form.requestSubmit();
        }
      }
    } else if (currentStep === 1) {
      navigate(`/wallet/products`);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) {
      if (formData?.hasBeenOpenedOrUsed !== undefined) {
        return true;
      }
      if (
        formData?.hasBeenOpenedOrUsed === true &&
        formData?.isProductDefective === true
      ) {
        if (
          formData?.refundReason &&
          formData?.additionalInfo &&
          formData?.imageUrls &&
          formData.imageUrls.length > 0
        ) {
          return true;
        }
        return false;
      }
      return formData?.hasBeenOpenedOrUsed !== undefined;
    }
    return true;
  };

  const handleFormChange = (data: ReturnReasonsFormData) => {
    setFormData(data);
  };

  const handleFormSubmit = async (data: ReturnReasonsFormData) => {
    setFormData(data);

    const returnData = {
      instanceId: instanceId,
      retailProductVariantId: selectedItem.packageId,
      hasBeenOpenedOrUsed: data.hasBeenOpenedOrUsed,
      isProductDefective: data.isProductDefective,
      additionalInfo: data.additionalInfo || "",
      refundReason: data.refundReason || "",
      imageUrls: data.imageUrls || [],
    };

    await post("/retailproducts/{platformId}/initiate-support/{orderId}", {
      platformId: platform.id,
      orderId: retailProductOrder.orderID,
      ...returnData,
    });

    // Move to final step
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(1);
      setIsTransitioning(false);
    }, 300);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ReturnReasonsAndMedia
            selectedItem={selectedItem}
            defaultValues={formData || {}}
            onChange={handleFormChange}
            onSubmit={handleFormSubmit}
          />
        );
      case 1:
        return (
          <div className="flex flex-col px-6 py-10 ">
            <div className="flex flex-col gap-6">
              <div className="font-serif text-[21px] font-medium leading-[24px]">
                Thanks for reaching out.
              </div>
              <Text variant="body2">
                Your request refund was submitted. You will receive an email
                with next steps in the next 3-5 business days.
              </Text>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TopNav
        startIconSVG={ArrowLeft}
        onStartIconClick={() => {
          navigate(-1);
        }}
      >
        Return Request
      </TopNav>
      <div className="flex min-h-screen flex-col justify-between overflow-visible pb-20">
        <div
          className={`transition-all duration-300 ease-in-out ${
            isTransitioning
              ? "-translate-y-4 transform opacity-0"
              : "translate-y-0 transform opacity-100"
          }`}
        >
          {renderStepContent()}
        </div>

        <div className="flex w-full px-4">
          <Button
            fullWidth
            style="primary"
            onClick={handleNext}
            disabled={!canProceed() || isTransitioning}
          >
            {currentStep === 0 &&
            formData?.imageUrls &&
            formData.imageUrls.length > 0
              ? "Submit"
              : currentStep === 1 || (currentStep === 0 && !formData?.imageUrls)
              ? "Return to Wallet"
              : "Next"}
          </Button>
        </div>
      </div>
    </>
  );
}
